import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Heart, Phone } from "lucide-react";
import { useClarityMode } from "@/contexts/ClarityContext";
import SeasonalBanner from "@/components/SeasonalBanner";
import Breadcrumbs from "@/components/breadcrumbs/Breadcrumbs";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasBanner, setHasBanner] = useState(false);
  const location = useLocation();
  const { isClarityMode } = useClarityMode();

  useEffect(() => {
    // Check if banner is visible by checking body class
    const checkBanner = () => {
      setHasBanner(document.body.classList.contains('has-banner'));
    };
    
    checkBanner();
    
    // Use MutationObserver to watch for class changes
    const observer = new MutationObserver(checkBanner);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    
    return () => observer.disconnect();
  }, []);

  const navItems = [
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
    { name: "Gallery", path: "/gallery" },
    { name: "About", path: "/about" },
    { name: "Contact", path: "/contact" },
    { name: "Blog", path: "/blog" },
    { name: "Loyalty", path: "/loyalty" },
    { name: "FAQ", path: "/faq" },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      {/* Seasonal Banner - positioned above sticky header */}
      <SeasonalBanner 
        id="winter-special-2024" 
        message="❄️ Winter Special: New customers get 10% off first grooming! Use code FIRST10 at checkout." 
        ctaText="Book Now" 
        ctaLink="/booking" 
        variant="promotion" 
      />
      
      <nav 
        className={`${isClarityMode ? '' : 'fixed'} left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b shadow-soft transition-all duration-300`}
        style={
          isClarityMode 
            ? { position: 'static' } 
            : { top: hasBanner ? 'var(--banner-height)' : 0 }
        }
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <Heart className="h-8 w-8 text-primary" fill="currentColor" />
              <span className="text-xl font-bold text-gradient-hero">
                Groomie Roomie
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`text-sm font-medium transition-colors duration-200 ${
                    isActive(item.path)
                      ? "text-primary border-b-2 border-primary"
                      : "text-muted-foreground hover:text-primary"
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              <Button variant="outline" size="sm" asChild>
                <a href="sms:+17209738622?&body=Hi!%20I'm%20interested%20in%20mobile%20grooming.%20Do%20you%20serve%20my%20area?%20%20My%20ZIP%20code%20is%3A" className="flex items-center gap-2" aria-label="Text Groomie Roomie at (720) 973-8622">
                  <Phone className="h-4 w-4" />
                  (720) 973-8622
                </a>
              </Button>
              <Button variant="hero" size="sm" asChild>
                <Link to="/booking">Book Now</Link>
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden p-2 rounded-md text-muted-foreground hover:text-primary"
              aria-label={isOpen ? "Close mobile menu" : "Open mobile menu"}
              aria-expanded={isOpen}
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden border-t bg-background/95 backdrop-blur-md">
            <div className="px-4 py-4 space-y-3">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  onClick={() => setIsOpen(false)}
                  className={`block py-2 text-sm font-medium transition-colors duration-200 ${
                    isActive(item.path)
                      ? "text-primary"
                      : "text-muted-foreground hover:text-primary"
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              <Button variant="outline" size="sm" className="w-full mt-4" asChild>
                <a href="sms:+17209738622?&body=Hi!%20I'm%20interested%20in%20mobile%20grooming.%20Do%20you%20serve%20my%20area?%20%20My%20ZIP%20code%20is%3A" className="flex items-center justify-center gap-2" aria-label="Text Groomie Roomie at (720) 973-8622">
                  <Phone className="h-5 w-5" />
                  (720) 973-8622
                </a>
              </Button>
              <Button variant="hero" size="sm" className="w-full mt-2" asChild>
                <Link to="/booking" onClick={() => setIsOpen(false)}>
                  Book Now
                </Link>
              </Button>
            </div>
          </div>
        )}
      </nav>
      
      {/* Global Breadcrumbs - positioned below header */}
      <Breadcrumbs />
    </>
  );
};

export default Navigation;